import type { CSSProperties, ReactNode } from 'react';
import { useTheme } from '../../context/ThemeProvider';
import { useLocale } from '../../context/DynamicLocaleProvider';
import { LanguageSwitch } from '../../components/LanguageSwitch';
import { SiteFooter } from '../../components/SiteFooter';
import { darkenHexColor, hexToRgbChannels, normalizeHexColor } from '../../utils/branding';
import styles from './authPages.module.css';

/** White-label branding for public flows (e.g. `/check?org=…`). */
export type AuthChromeBrand = {
  name: string;
  logoUrl?: string | null;
  heroImageUrl?: string | null;
  primaryColor?: string | null;
};

type AuthChromeProps = {
  children: ReactNode;
  heroImageSrc?: string;
  brand?: AuthChromeBrand;
};

/**
 * Derived tokens like `--global-button-color: var(--accent)` resolve at
 * `:root`, so overriding `--accent` alone would not restyle buttons — every
 * derived token is re-declared here with the brand color.
 */
function brandStyle(brand: AuthChromeBrand | undefined): CSSProperties | undefined {
  const accent = normalizeHexColor(brand?.primaryColor);
  if (!accent) return undefined;
  const hover = darkenHexColor(accent);
  const rgb = hexToRgbChannels(accent);
  return {
    '--accent': accent,
    '--accent-fade': hover,
    '--global-button-color': accent,
    '--global-button-color-hover': hover,
    '--primary-color': accent,
    '--amethyst-smoke': accent,
    ...(rgb ? { '--primary-color-rgb': rgb } : {}),
    '--focus-border': `color-mix(in srgb, ${accent} 60%, transparent)`,
    '--focus-ring': `0 0 0 3px color-mix(in srgb, ${accent} 14%, transparent)`,
  } as CSSProperties;
}

function ThemeToggleIcon({ theme }: { theme: 'light' | 'dark' }) {
  if (theme === 'light') {
    return (
      <svg className={styles.themeToggleIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M20 14.5A8.5 8.5 0 0 1 9.5 4 7 7 0 1 0 20 14.5Z"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg className={styles.themeToggleIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function TaglineWithBreak({ text }: { text: string }) {
  // Always break after the first sentence, in every language.
  const match = text.match(/^(.*?[.!?…])\s+(\S[\s\S]*)$/);
  if (!match) {
    return <>{text}</>;
  }
  return (
    <>
      {match[1]}
      <br />
      {match[2]}
    </>
  );
}

export function AuthChrome({ children, heroImageSrc, brand }: AuthChromeProps) {
  const { theme, toggleTheme } = useTheme();
  const { t } = useLocale();

  return (
    <div className={styles.shell} style={brandStyle(brand)}>
      <aside className={styles.heroPanel} aria-hidden="true">
        <img
          className={styles.heroImage}
          src={brand?.heroImageUrl ?? heroImageSrc ?? '/assets/images/craft-hero-background.png'}
          alt=""
        />
        <div className={styles.heroScrim} />
        <div className={styles.heroContent}>
          {brand ? (
            brand.logoUrl ? (
              <img className={styles.heroBrandLogo} src={brand.logoUrl} alt="" />
            ) : (
              <span className={styles.heroBrandName}>{brand.name}</span>
            )
          ) : (
            <img
              className={styles.heroLogo}
              src="/assets/logos/craftin-logo-white.svg"
              alt=""
              width={222}
              height={50}
            />
          )}
          <p className={styles.heroTagline}>
            <TaglineWithBreak text={t('auth_hero_tagline')} />
          </p>
        </div>
      </aside>
      <div className={styles.formPanel}>
        <header className={styles.topBar}>
          <div className={styles.brand}>
            {brand ? (
              <>
                {brand.logoUrl && <img className={styles.brandLogoCustom} src={brand.logoUrl} alt="" />}
                <div className={styles.brandText}>
                  <span className={styles.brandTitle}>{brand.name}</span>
                  <span className={styles.brandSub}>{t('check_powered_by')}</span>
                </div>
              </>
            ) : (
              <>
                <img
                  className={styles.brandLogo}
                  src="/assets/logos/crafting-icon.svg"
                  alt=""
                  width={32}
                  height={32}
                />
                <div className={styles.brandText}>
                  <span className={styles.brandTitle}>Craftin</span>
                  <span className={styles.brandSub}>Platform</span>
                </div>
              </>
            )}
          </div>
          <div className={styles.topBarActions}>
            <button
              type="button"
              className={styles.themeToggle}
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-pressed={theme === 'light'}
            >
              <ThemeToggleIcon theme={theme} />
            </button>
            <LanguageSwitch />
          </div>
        </header>
        <main className={styles.formMain}>
          <div className={styles.card}>{children}</div>
        </main>
        <SiteFooter className={styles.siteFooter} variant="auth" />
      </div>
    </div>
  );
}
